/**
 * Turning the catalogue into a list of settings somebody can change.
 *
 * THE FEATURE'S OWN ADVICE WAS UNREACHABLE BEFORE THIS. The verdict panel says "raise
 * `new_page_min`, currently the default 3, to about 6" — and there was no box to type 6
 * into. Every knob the server can take was already accepted by the API and served with
 * its provenance; nothing rendered any of it. A knob nobody can find is not a control,
 * which is the sentence `verdict.py` opens with.
 *
 * ONE KNOB CAN DRIVE SEVERAL ALERTS, and that is the shape that makes this worth its own
 * module. `price_delta_pct` is the bar for both a price rise and a price cut;
 * `rating_drop_min` covers a rating sliding and climbing. A settings screen listing one
 * row per ALERT would show the same number twice and invite two different answers to it,
 * so the rows are per KNOB and each names the alerts it governs.
 *
 * Pure and dependency-free so CRA's jest can test it, for the reason
 * `frontend-pure-logic-own-module` records.
 */
import type { WatchCatalogue, WatchThreshold } from '../../types';

export interface Knob {
  /** The server-side key, e.g. `new_page_min`. Sent back verbatim. */
  key: string;
  /** The current value, or null when the catalogue declares no default. */
  value: number | null;
  /** "3 (default)" / "10 (your setting on this subject)" — written server-side, beside the rule. */
  described: string;
  /** Whether the user has set this, as opposed to inheriting it. */
  isOverride: boolean;
  /** The alerts this one number governs, by their human titles. */
  governs: string[];
  /** Which family they belong to, for grouping. Empty when they span several. */
  family: string;
  /** True when every alert it governs fires on a RATE of change rather than a level. */
  derivative: boolean;
}

/**
 * The knobs in play, joined to what they actually control.
 *
 * Driven by the catalogue rather than a list here, so a signal that gains a threshold shows
 * up as a setting without a frontend change — the same reason the probe list is served.
 */
export function knobsFor(
  thresholds: WatchThreshold[],
  catalogue: WatchCatalogue | null,
): Knob[] {
  const governs = new Map<string, { titles: string[]; families: Set<string>; derivative: boolean }>();

  for (const probe of catalogue?.probes ?? []) {
    for (const signal of probe.signals ?? []) {
      if (!signal.threshold_key) continue;
      const entry = governs.get(signal.threshold_key) ?? {
        titles: [],
        families: new Set<string>(),
        derivative: true,
      };
      if (!entry.titles.includes(signal.title)) entry.titles.push(signal.title);
      entry.families.add(signal.family);
      // Derivative only when EVERY alert it governs is. `new_page_min` gates a level, and saying
      // otherwise would be a small lie on a tooltip.
      entry.derivative = entry.derivative && Boolean(signal.derivative);
      governs.set(signal.threshold_key, entry);
    }
  }

  return (thresholds ?? [])
    .map((t) => {
      const found = governs.get(t.key);
      return {
        key: t.key,
        value: t.value,
        described: t.described,
        isOverride: t.is_override,
        governs: found?.titles ?? [],
        family:
          found && found.families.size === 1
            ? Array.from(found.families)[0]
            : '',
        derivative: found?.derivative ?? false,
      };
    })
    // A knob governing nothing this subject can be alerted about is not a setting here — it belongs
    // to a probe this kind of subject cannot have.
    .filter((k) => k.governs.length > 0);
}

/** Knobs grouped by family, in the catalogue's own family order. */
export function byFamily(
  knobs: Knob[],
  catalogue: WatchCatalogue | null,
): { family: string; title: string; knobs: Knob[] }[] {
  const titles = new Map((catalogue?.families ?? []).map((f) => [f.key, f.title]));
  const order = (catalogue?.families ?? []).map((f) => f.key);
  const groups = new Map<string, Knob[]>();
  for (const knob of knobs) {
    const key = knob.family || 'other';
    groups.set(key, [...(groups.get(key) ?? []), knob]);
  }
  return Array.from(groups.entries())
    .sort((a, b) => {
      const ai = order.indexOf(a[0]);
      const bi = order.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    })
    .map(([family, ks]) => ({
      family,
      title: titles.get(family) ?? 'Other',
      knobs: ks,
    }));
}

/**
 * A typed box into a value to send, or `undefined` when it should not be sent at all.
 *
 * EMPTY MEANS "BACK TO THE DEFAULT", NOT ZERO. A user clearing the box wants to stop
 * overriding, and a zero threshold means "alert on everything" — the two could not be
 * further apart, and a form that conflated them would turn a tidy-up into a flood.
 * `null` is the server's own signal for "remove my override".
 */
export function parseKnob(raw: string): number | null | undefined {
  const text = raw.trim();
  if (!text) return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
}

/**
 * The payload for a thresholds PATCH: only what CHANGED.
 *
 * Sending the whole set would write an override for every knob the user merely looked at,
 * which is exactly the "override nobody meant to create" that `thresholds_out` carries
 * provenance to prevent.
 */
export function changedKnobs(
  knobs: Knob[],
  edits: Record<string, string>,
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const knob of knobs) {
    const raw = edits[knob.key];
    if (raw === undefined) continue;
    const parsed = parseKnob(raw);
    if (parsed === undefined) continue;
    // TYPING THE CURRENT NUMBER IS NOT A CHANGE, whether or not it is already an
    // override. Somebody who types 3 into a knob showing "3 (default)" is confirming
    // what they read, not asking to own it — and owning it means it will not follow the
    // default when that is retuned. That is the override `thresholds.py` carries
    // provenance strings to prevent, and an earlier version of this line created one.
    const same = parsed === null ? !knob.isOverride : parsed === knob.value;
    if (!same) out[knob.key] = parsed;
  }
  return out;
}

/** One line on what a knob does, for the row's hint. */
export function explain(knob: Knob): string {
  const list =
    knob.governs.length > 2
      ? `${knob.governs.slice(0, 2).join(', ')} and ${knob.governs.length - 2} more`
      : knob.governs.join(' and ');
  const shape = knob.derivative
    ? 'Only a change this big or bigger is reported.'
    : 'Anything under this is not reported.';
  return `${list}. ${shape}`;
}
